import React, { useState, useEffect } from 'react';
import { Typography, Grid, Paper, Button, CircularProgress, Box, Checkbox } from '@material-ui/core';
import '../assets/styles/Step3.css';
import api, {consoleDevLog} from "../apiConfig";
import ComparablesTable from './ComparablesTable/ComparablesTable';
import CompareModal from './CompareModal';
import {comparisonFields} from "./utils/fields";
import {returnSubjectPropParams, getDisplayAddress} from "./utils/utils";

const Step3 = ({
  subjectProperty,
  currentStep,
  finalSelectedHomes,
  setFinalSelectedHomes,
  adjustments,
  setAdjustments,
  setCurrentStep,
  overrideSubjectPropertyNum,
}) => {
  const [loading, setLoading] = useState(false);
  const [comparisonHome, setComparisonHome] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'grid' or 'table'
  const [isCompareModalOpen, setCompareModalOpen] = useState(false);

  // Fetch price adjustments when the component mounts or finalSelectedHomes change
  useEffect(() => {
    consoleDevLog("Subject Property: ", subjectProperty, subjectProperty.Tot_park_spcs)
    const fetchPriceAdjustments = async (comparableNums) => {
      try {
        setLoading(true);
        consoleDevLog("Step3 - Making API call with overrideSubjectPropertyNum:", overrideSubjectPropertyNum);
        
        const response = await api.post("/homes/adjustments", {
          subjectPropertyNum: subjectProperty.Ml_num,
          comparables: comparableNums,
          subjectPropParams: returnSubjectPropParams(subjectProperty),
          overrideSubjectPropertyNum: overrideSubjectPropertyNum
        });

        consoleDevLog("Step3 - API call args:", {
          subjectPropertyNum: subjectProperty.Ml_num,
          comparables: comparableNums,
          subjectPropParams: returnSubjectPropParams(subjectProperty),
          overrideSubjectPropertyNum: overrideSubjectPropertyNum
        });

        setAdjustments(prevAdjustments => ({
          ...prevAdjustments,
          ...response.data,
        }));
      } catch (error) {
        console.error("Error fetching price adjustments:", error);
      } finally {
        setLoading(false);
      }
    };

    if (finalSelectedHomes.length > 0) {
      consoleDevLog("Step3 - Fetching price adjustments for all comparables due to subject property or comparables change.");
      fetchPriceAdjustments(finalSelectedHomes.map(home => home.Ml_num).join(','));
    }
  }, [finalSelectedHomes, subjectProperty]);

  const handleCompareClick = (home) => {
    setComparisonHome(home);
    setCompareModalOpen(true);
  };

  const handleComparisonClose = () => {
    setCompareModalOpen(false);
    setComparisonHome(null);
  };

  const handleViewToggle = (view) => {
    setViewMode(view);
  };

  const handleAdjustmentClick = (home, adjustmentIndex) => {
    const updatedAdjustments = adjustments[home.Ml_num].map((adjustment, index) =>
      index === adjustmentIndex ? { ...adjustment, selected: !adjustment.selected } : adjustment
    );
    setAdjustments(prevAdjustments => ({
      ...prevAdjustments,
      [home.Ml_num]: updatedAdjustments,
    }));
  };

  return (
    <div className={currentStep === 3 ? 'active-step' : 'inactive-step'}>
      {viewMode === 'table' ? (
        <ComparablesTable
          subjectProperty={subjectProperty}
          finalSelectedHomes={finalSelectedHomes}
          adjustments={adjustments}
          setAdjustments={setAdjustments}
          setCurrentStep={setCurrentStep}
          loading={loading}
          step={3}
        />
      ) : (
        <Grid container spacing={3} alignItems="stretch" className="step3-container">
          <Grid item xs={3}>
            <Paper className="home-paper subject-property-card lavender-background">
              <img 
                src={
                  subjectProperty.image 
                    ? URL.createObjectURL(subjectProperty.image) 
                    : `https://realtime-home-images.s3.amazonaws.com/${subjectProperty.Ml_num}/1.jpeg`
                } 
                alt={getDisplayAddress(subjectProperty)} 
                className="comparable-img" 
              />
              <Typography variant="h5" className="home-text">{getDisplayAddress(subjectProperty)}</Typography>
              <Typography className="home-text">Original Price: ${subjectProperty.Lp_dol}</Typography>
              <Typography className="home-text">Status: {subjectProperty.Lsc}</Typography>
              <Typography className="home-text">Square Feet: {subjectProperty.Sqft ?? "N/A"}</Typography>
              <Typography className="home-text">Bedrooms: {subjectProperty.Br ?? "N/A"}</Typography>
              <Typography className="home-text">Bathrooms: {subjectProperty.Bath_tot ?? "N/A"}</Typography>
              <Typography className="home-text">Lot Size: {subjectProperty.Front_ft && subjectProperty.Depth ? `${Math.round(subjectProperty.Front_ft * subjectProperty.Depth)} sqft` : "N/A"}</Typography>
            </Paper>
          </Grid>

          {finalSelectedHomes.map(home => (
            <Grid item xs={3} key={home.id}>
              <Paper className="home-paper">
                <Button variant="contained" color="primary" style={{ position: 'absolute', top: '10px', left: '10px' }} onClick={() => handleCompareClick(home)}>
                  Compare
                </Button>
                <img src={`https://realtime-home-images.s3.amazonaws.com/${home.Ml_num}/1.jpeg`} alt={getDisplayAddress(home)} className="comparable-img" />
                <Typography variant="h5" className="home-text">{getDisplayAddress(home)}</Typography>
                <Typography className="home-text">Original Price: ${home.Sp_dol}</Typography>
                <Typography className="home-text">Status: {home.Lsc}</Typography>
                <Typography className="home-text">Square Feet: {home.Sqft ?? "N/A"}</Typography>
                <Typography className="home-text">Bedrooms: {home.Br ?? "N/A"}</Typography>
                <Typography className="home-text">Bathrooms: {home.Bath_tot ?? "N/A"}</Typography>
                <Typography className="home-text">Lot Size: {home.Front_ft && home.Depth ? `${Math.round(home.Front_ft * home.Depth)} sqft` : "N/A"}</Typography>
                <Typography variant="h6" className="suggested-price-header">Suggested Price Adjustments</Typography>

                {loading ? (
                  <div className="loading-container">
                    <CircularProgress />
                  </div>
                ) : (
                  <div className="suggested-price-adjustments">
                    <ul className="adjustments-list">
                      <div className="adjustments-scroll">
                        {adjustments[home.Ml_num] && adjustments[home.Ml_num].map((adjustment, index) => (
                          <div key={index} className="adjustment-item">
                            <Checkbox
                              checked={adjustment.selected || false}
                              onChange={() => handleAdjustmentClick(home, index)}
                              color="primary"
                            />
                            <Typography variant="body2" display="inline">
                              {adjustment.description}: {adjustment.amount > 0 ? `+${adjustment.amount}` : adjustment.amount}
                            </Typography>
                          </div>
                        ))}
                      </div>
                    </ul>
                  </div>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {comparisonHome && (
        <CompareModal
          subjectProperty={subjectProperty}
          comparableHome={comparisonHome}
          open={isCompareModalOpen}
          onClose={handleComparisonClose}
          comparisonFields={comparisonFields}
        />
      )}
    </div>
  );
};

export default Step3;
